import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import type { WricefDecisionSummary } from "@/api/wricef-decisions";
import {
  makeExtensionTargetMeta,
  makeWricefTypeMeta,
} from "@/components/projects/diagnostic/diagnosticMeta";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { formatCount } from "@/lib/format";
import { tp } from "@/lib/plural";

/**
 * "Escopo decidido" no Effort Estimation:
 * Exibe os totais ao vivo dos itens `in_scope` definidos pelo arquiteto (via `useWricefDecisionSummary`),
 * alinhado aos cards do Gate A e WRICEF Catalog.
 *
 * Estrutura visual:
 * - `DecidedScopeMetrics` (números): fica na mesma fileira dos cards congelados.
 * - `DecidedScopeBreakdown` (detalhamento por estratégia/letra): fica em card separado.
 *
 * Nota: O `cost_breakdown` congelado da última execução continua ao lado ("estimativa automática da análise").
 */

function MetricCard({
  testId,
  label,
  value,
  sub,
  className,
}: {
  testId: string;
  label: string;
  value: string;
  sub: ReactNode;
  className?: string;
}) {
  return (
    <Card data-testid={testId} className={className}>
      <CardContent className="py-4">
        <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
        <div className="mt-1 text-3xl font-semibold tabular-nums">{value}</div>
        <div className="text-xs text-muted-foreground">{sub}</div>
      </CardContent>
    </Card>
  );
}

/** Os números do escopo decidido: para a MESMA fileira dos cards congelados. */
export function DecidedScopeMetrics({ summary }: { summary?: WricefDecisionSummary }) {
  const { t } = useTranslation();
  if (!summary || !summary.universe) return null;

  const days = summary.in_scope_hours / 8;
  const pendingCount = summary.by_decision.client_validation ?? 0;
  const pendingHours = summary.hours_by_decision.client_validation ?? 0;

  return (
    <>
      <MetricCard
        testId="decided-effort-card"
        className="border-primary/30"
        label={t("Decided effort")}
        value={`${formatCount(summary.in_scope_hours)}h`}
        sub={t("{{days}} person-days", { days: formatCount(days, { decimals: 1 }) })}
      />
      {pendingCount > 0 && (
        <MetricCard
          testId="decided-open-effort-card"
          className="border-amber-300 bg-amber-50/40"
          label={t("Open effort")}
          value={tp(t, pendingCount, "{{n}} object · {{hours}} h", "{{n}} objects · {{hours}} h", {
            n: formatCount(pendingCount),
            hours: formatCount(pendingHours),
          })}
          sub={t(
            "Waiting for the client to validate. These hours are not in the scope total yet.",
          )}
        />
      )}
    </>
  );
}

/** O detalhamento do escopo decidido: por letra WRICEF e por estratégia. */
export function DecidedScopeBreakdown({ summary }: { summary?: WricefDecisionSummary }) {
  const { t } = useTranslation();
  if (!summary || !summary.universe) return null;

  const letterMeta = makeWricefTypeMeta(t);
  const strategyMeta = makeExtensionTargetMeta(t);

  const objectsAndHours = (objects: number, hours: number) =>
    tp(t, objects, "{{n}} object · {{hours}} h", "{{n}} objects · {{hours}} h", {
      n: formatCount(objects),
      hours: formatCount(hours),
    });

  const letterEntries = Object.entries(summary.by_wricef_decision)
    .map(([letter, byDecision]) => [letter, byDecision.in_scope ?? 0] as const)
    .filter(([, count]) => count > 0)
    .sort(([, a], [, b]) => b - a);
  const maxLetterCount = letterEntries.reduce((acc, [, v]) => Math.max(acc, v), 0);

  const strategyEntries = Object.entries(summary.in_scope_by_strategy)
    .filter(([, v]) => v.count > 0)
    .sort(([, a], [, b]) => b.hours - a.hours);
  const maxStrategyHours = strategyEntries.reduce((acc, [, v]) => Math.max(acc, v.hours), 0);

  if (letterEntries.length === 0 && strategyEntries.length === 0) return null;

  return (
    <Card data-testid="decided-scope-breakdown" className="shadow-none border">
      <CardHeader className="pb-3">
        <CardTitle className="text-base">{t("Decided scope")}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {letterEntries.length > 0 && (
          <div className="space-y-2">
            <div className="text-xs font-medium text-muted-foreground">
              {t("In scope by WRICEF category")}
            </div>
            {letterEntries.map(([letter, count]) => (
              <DecidedScopeBar
                key={letter}
                badge={letter}
                label={letterMeta[letter]?.label ?? "?"}
                value={count}
                maxValue={maxLetterCount}
                displayValue={tp(t, count, "{{n}} object", "{{n}} objects", {
                  n: formatCount(count),
                })}
              />
            ))}
          </div>
        )}

        {strategyEntries.length > 0 && (
          <div className="space-y-2">
            <div className="text-xs font-medium text-muted-foreground">
              {t("In scope by strategy")}
            </div>
            {strategyEntries.map(([strategy, entry]) => (
              <DecidedScopeBar
                key={strategy}
                label={strategyMeta[strategy as keyof typeof strategyMeta]?.label ?? strategy}
                value={entry.hours}
                maxValue={maxStrategyHours}
                displayValue={objectsAndHours(entry.count, entry.hours)}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/** Uma linha de barra: rótulo à esquerda, barra proporcional, valor à direita. */
function DecidedScopeBar({
  badge,
  label,
  value,
  maxValue,
  displayValue,
}: {
  badge?: string;
  label: string;
  value: number;
  maxValue: number;
  displayValue: string;
}) {
  const pct = maxValue > 0 ? Math.round((value / maxValue) * 100) : 0;
  return (
    <div className="flex items-center gap-3 text-xs">
      <div className="w-32 shrink-0 truncate text-muted-foreground">
        {badge && <span className="mr-1 font-mono font-semibold text-foreground">{badge}</span>}
        {label}
      </div>
      <div className="flex-grow h-2 rounded-full bg-muted overflow-hidden">
        <div className="h-full bg-primary" style={{ width: `${pct}%` }} />
      </div>
      <div className="w-36 shrink-0 text-right tabular-nums">{displayValue}</div>
    </div>
  );
}
